import './Login.scss';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { toast, ToastContainer } from 'react-toastify';

import * as wjCore from '@grapecity/wijmo';
import * as wjInput from '@grapecity/wijmo.input';
import { ComboBox } from '@grapecity/wijmo.react.input';

import { ApiClient } from '../apiClient';
import * as Common from '../common';

import logo from '../images/elixirs.png';

interface LicenseState {
  aerith: boolean;
  sido: boolean;
  atHome: boolean;
}

interface LoginResult {
  succeeded: boolean;
  message?: string;
  returnUrl?: string | null;
  isAdmin?: boolean;
  isCsInfo?: boolean;
}

interface UserData {
  userId: string;
  userName: string;
  sessionId: string;
  auth: number;
  expired: boolean;
  isPrincipal: boolean;
}

export const Login = () => {
  const toastContainerId = 'Login';

  const [returnUrl] = useState<string | null>(() => {
    const params = new URLSearchParams(location.search);

    const returnUrl = params.get('ReturnUrl');

    if (!returnUrl) {
      return null;
    }

    const regexp = new RegExp('^' + (wjCore.escapeRegExp(window.aerith.applicationRoot || '/') + '/*$'), 'i');

    if (regexp.test(returnUrl)) {
      return null;
    }

    return returnUrl;
  });

  const [licenseState, setLicenseState] = useState<LicenseState>();
  const [isBusy, setBusyState] = useState<boolean>(false);

  const userNameComboBox = useRef<ComboBox>();
  const passwordComboBox = useRef<ComboBox>();

  useEffect(() => {
    if (returnUrl) {
      toast.warn('サーバーとの通信が発生しない状態でしばらく時間が経過しましたので、安全のためにログアウトしました。', {
        containerId: toastContainerId,
        autoClose: false,
      });
    }
  }, []);

  useEffect(() => {
    ApiClient.get<LicenseState>('~/Common/LicenseState')
      .then((state) => {
        setLicenseState(state);
      })
      .catch((error) => {
        console.error(error);

        toast.warn('ライセンスを確認できませんでした。', { containerId: toastContainerId });
      });
  }, []);

  const handleChallengeButtonClick = useCallback(() => {
    const userNameControl: wjInput.ComboBox | undefined = userNameComboBox.current?.control;
    const passwordControl: wjInput.ComboBox | undefined = passwordComboBox.current?.control;

    const userName = userNameControl?.text ?? '';
    const password = passwordControl?.text ?? '';

    if (!userName || !password) {
      toast.info('ログインIDとパスワードを入力してください。', { containerId: toastContainerId });

      if (userName) {
        passwordControl?.focus();
      } else {
        userNameControl?.focus();
      }

      return;
    }

    setBusyState(true);

    try {
      const data = {
        userName: userName,
        password: password,
        returnUrl: returnUrl,
      };

      ApiClient.post<LoginResult>('/Login/Login', data)
        .then((result) => {
          if (result.succeeded) {
            if (window.aerith.isSynthesisEnabled) {
              if (result?.isCsInfo) {
                toast.success('ログインしました。', { containerId: toastContainerId });

                if (result.returnUrl) {
                  // リダイレクトされていた場合は元のページへ移動する
                  location.href = result.returnUrl;
                } else if (licenseState && !licenseState.sido && licenseState.atHome) {
                  // 服薬指導のライセンスがない、かつ @HOME+ のライセンスがある場合は訪問指導ページへ移動する
                  location.href = Common.getFullPathName('~/VisitSchedule');
                } else {
                  location.href = Common.getFullPathName('~/Patient');
                }
              } else if (result?.isAdmin) {
                toast.warn('デフォルト店舗が設定されていません。', { containerId: toastContainerId });
                location.href = Common.getFullPathName('/Admin');
              } else {
                toast.error('表示可能な店舗がありません。', { containerId: toastContainerId });
              }
            } else {
              toast.success('ログインしました。', { containerId: toastContainerId });

              if (result.returnUrl) {
                // リダイレクトされていた場合は元のページへ移動する
                location.href = result.returnUrl;
              } else if (licenseState && !licenseState.sido && licenseState.atHome) {
                // 服薬指導のライセンスがない、かつ @HOME+ のライセンスがある場合は訪問指導ページへ移動する
                location.href = Common.getFullPathName('~/VisitSchedule');
              } else {
                location.href = Common.getFullPathName('~/Patient');
              }
            }
          } else {
            toast.error(result.message, { containerId: toastContainerId });
          }
        })
        .catch((error) => {
          console.error(error);

          toast.error('ログインサーバーとの通信に失敗しました。', { containerId: toastContainerId });
        })
        .finally(() => {
          setBusyState(false);
        });
    } catch (error) {
      console.error(error);

      setBusyState(false);
    }
  }, [licenseState]);

  // タブレット薬歴のライセンスがない場合はメッセージを表示する
  // ライセンス状態の問い合わせ中や通信障害等による失敗時においては、ライセンスがあるものとしてログインフォームを表示する
  if (licenseState && !licenseState.aerith) {
    return (
      <div className="login">
        <div className="container w-auto">
          <div className="jumbotron jumbotron-fluid nameboard">
            <img className="logo" src={logo} />
          </div>

          <div className="noLicense">
            「Ｓ薬歴」の利用ライセンスを確認できませんでした。
            <br />
            このメッセージが何度も表示される場合は、販売店にお問合せください。
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <div className="container w-auto">
        <div className="nameboard jumbotron jumbotron-fluid">
          <img className="logo" src={logo} />
        </div>

        <div className="row justify-content-center">
          <form id="login" className="loginForm">
            <div className="text-black-50 text-right">Ver.{process.env.npm_package_version}</div>
            <div className="form-group">
              <div className="wj-labeled-input wide">
                <ComboBox
                  ref={userNameComboBox}
                  name="userName"
                  type="text"
                  required="required"
                  autocomplete="username"
                />
                <label htmlFor="userName">ログインID</label>
                <div className="wj-error" tabIndex={-1}>
                  ログインIDが必要です
                </div>
              </div>
            </div>

            <div className="form-group">
              <div className="wj-labeled-input wide">
                <ComboBox
                  ref={passwordComboBox}
                  name="password"
                  type="password"
                  required="required"
                  autocomplete="current-password"
                />
                <label htmlFor="password">パスワード</label>
                <div className="wj-error" tabIndex={-1}>
                  パスワードを入力してください
                </div>
              </div>
            </div>

            <div className="loginForm_actions text-right">
              <button
                className="action challenge btn btn-primary"
                type="button"
                disabled={isBusy}
                onClick={handleChallengeButtonClick}
              >
                ログイン
              </button>
            </div>
          </form>
        </div>
      </div>

      <ToastContainer enableMultiContainer={true} containerId={toastContainerId} position="top-center" />
    </div>
  );
};
